import { useEffect, useRef } from "react";
import Link from "next/link";

const MegaMenu = ({ loading, children = [], isService, onCloseMenu }) => {
  // ✅ prevents random close when mouse re-enters quickly
  const closeTimerRef = useRef(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearCloseTimer();
  }, []);

  // const chunkIntoColumns = (items = [], columnCount = 5) => {
  //   if (!items.length) return [];

  //   const perColumn = Math.ceil(items.length / columnCount);

  //   return Array.from({ length: columnCount }, (_, index) =>
  //     items.slice(index * perColumn, (index + 1) * perColumn),
  //   );
  // };

  const chunkIntoColumnsByWeight = (items = [], columnCount = 5) => {
    if (!items.length) return [];

    const columns = Array.from({ length: columnCount }, () => ({
      items: [],
      weight: 0,
    }));

    items.forEach((item) => {
      const weight = 1 + (item.services?.length || 0);
      const lightestColumn = columns.reduce((min, col) =>
        col.weight < min.weight ? col : min,
      );

      lightestColumn.items.push(item);
      lightestColumn.weight += weight;
    });

    return columns.map((col) => col.items).filter((col) => col.length > 0);
  };

  const serviceColumns = isService ? chunkIntoColumnsByWeight(children, 5) : [];
  // const serviceColumns = isService ? chunkIntoColumns(children, 5) : [];
  return (
    <div
      className="max-h-[500px] overflow-y-auto custom_scrollbar"
      onMouseEnter={clearCloseTimer}
      onMouseLeave={() => {
        clearCloseTimer();
        closeTimerRef.current = setTimeout(() => {
          onCloseMenu?.();
        }, 250);
      }}
    >
      {/* ✅ Show skeleton on first hover until data arrives */}
      {loading || children.length === 0 ? (
        <div className="grid grid-cols-4">
          {[1, 2, 3, 4].map((x) => (
            <div className="space-y-4 animate-pulse p-4" key={x}>
              <div className="h-6 bg-gray-300 rounded w-1/2" />
              <div className="h-6 bg-gray-300 rounded" />
              <div className="h-6 bg-gray-300 rounded" />
            </div>
          ))}
        </div>
      ) : isService ? (
        <div className="col-span-3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-6">
          {serviceColumns.map((column, colIdx) => (
            <div key={colIdx}>
              {column.map((parent, pIdx) => (
                <div key={parent._id || pIdx} className="mb-5">
                  <h2 className="block text-[14px] xl:text-[16px] leading-[170%] px-2 py-1.5 font-semibold text-white capitalize">
                    {parent.title}
                  </h2>

                  <div className="space-y-1">
                    {parent.services?.map((sub, subIdx) => (
                      <Link
                        key={sub._id || subIdx}
                        href={`/${sub.fullSlug_en}`}
                        className="block text-[13px] xl:text-[15px] leading-[170%] px-2 py-1.5 font-medium text-white/50 hover:text-white capitalize no-underline"
                      >
                        {sub.menuname}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-[0.8fr_0.8fr_1.5fr_1.5fr_1.5fr] gap-4">
          {/* Left Column 1: First 9 Main Items (no children) */}
          <div className="col-span-1">
            {children
              ?.filter((service) => (service?.children?.length || 0) === 0)
              ?.slice(0, 9)
              ?.map((child, idx) => (
                <Link
                  key={idx}
                  href={
                    isService ? `/${child.slug}` : `/industry/${child.slug}`
                  }
                  className="block text-[16px] leading-[170%] px-3 py-1.5 font-semibold text-white capitalize no-underline"
                >
                  {isService ? child.menuname : child.name}
                </Link>
              ))}
          </div>

          {/* Left Column 2: Remaining Main Items (no children) */}
          <div className="col-span-1">
            {children
              ?.filter((service) => (service?.children?.length || 0) === 0)
              ?.slice(9)
              ?.map((child, idx) => (
                <Link
                  key={idx + 9}
                  href={
                    isService ? `/${child.slug}` : `/industry/${child.slug}`
                  }
                  className="block text-[16px] leading-[170%] px-3 py-1.5 font-semibold text-white capitalize no-underline"
                >
                  {isService ? child.menuname : child.name}
                </Link>
              ))}
          </div>

          {/* Right Columns: All services with children and grandchildren */}
          <div className="col-span-3 grid grid-cols-3 gap-3">
            {/* LEFT COLUMN – Parents with ONLY children (no grandchildren) */}
            <div>
              {children
                ?.filter(
                  (service) =>
                    (service?.children?.length || 0) > 0 &&
                    !service.children?.some(
                      (sub) => (sub?.grandchildren?.length || 0) > 0,
                    ),
                )
                ?.slice(0, 1)
                ?.map((parent, pIdx) => (
                  <div key={pIdx} className="mb-3">
                    <Link
                      href={
                        isService
                          ? `/${parent.slug}`
                          : `/industry/${parent.slug}`
                      }
                      className="block text-[16px] leading-[170%] px-3 py-1.5 font-semibold text-white capitalize no-underline"
                    >
                      {isService ? parent.menuname : parent.name}
                    </Link>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        {parent.children?.slice(0, 5).map((sub, subIdx) => (
                          <Link
                            key={subIdx}
                            href={
                              isService
                                ? `/${parent.slug}/${sub.slug}`
                                : `/industry/${sub.slug}`
                            }
                            className="block text-[16px] leading-[170%] px-3 py-1.5 font-semibold text-white/50 hover:text-white capitalize no-underline"
                          >
                            {isService ? sub.menuname : sub.name}
                          </Link>
                        ))}
                      </div>

                      <div className="space-y-1">
                        {parent.children?.slice(5).map((sub, subIdx) => (
                          <Link
                            key={subIdx + 5}
                            href={
                              isService
                                ? `/${parent.slug}/${sub.slug}`
                                : `/industry/${sub.slug}`
                            }
                            className="block text-[16px] leading-[170%] px-3 py-1.5 font-semibold text-white/50 hover:text-white capitalize no-underline"
                          >
                            {isService ? sub.menuname : sub.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* MIDDLE COLUMN – remaining parents with ONLY children */}
            <div>
              {children
                ?.filter(
                  (service) =>
                    (service?.children?.length || 0) > 0 &&
                    !service.children?.some(
                      (sub) => (sub?.grandchildren?.length || 0) > 0,
                    ),
                )
                ?.slice(1)
                ?.map((parent, pIdx) => (
                  <div key={pIdx + 1} className="mb-3">
                    <Link
                      href={
                        isService
                          ? `/${parent.slug}`
                          : `/industry/${parent.slug}`
                      }
                      className="block text-[16px] leading-[170%] px-3 py-1.5 font-semibold text-white capitalize no-underline"
                    >
                      {isService ? parent.menuname : parent.name}
                    </Link>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        {parent.children?.slice(0, 7).map((sub, subIdx) => (
                          <Link
                            key={subIdx}
                            href={
                              isService
                                ? `/${parent.slug}/${sub.slug}`
                                : `/industry/${sub.slug}`
                            }
                            className="block text-[16px] leading-[170%] px-3 py-1.5 font-semibold text-white/50 hover:text-white capitalize no-underline"
                          >
                            {isService ? sub.menuname : sub.name}
                          </Link>
                        ))}
                      </div>

                      <div className="space-y-1">
                        {parent.children?.slice(7).map((sub, subIdx) => (
                          <Link
                            key={subIdx + 7}
                            href={
                              isService
                                ? `/${parent.slug}/${sub.slug}`
                                : `/industry/${sub.slug}`
                            }
                            className="block text-[16px] leading-[170%] px-3 py-1.5 font-semibold text-white/50 hover:text-white capitalize no-underline"
                          >
                            {isService ? sub.menuname : sub.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* RIGHT COLUMN – Parents with Grandchildren */}
            <div>
              {children
                ?.filter(
                  (service) =>
                    (service?.children?.length || 0) > 0 &&
                    service.children?.some(
                      (sub) => (sub?.grandchildren?.length || 0) > 0,
                    ),
                )
                ?.map((parent, pIdx) => (
                  <div key={pIdx} className="mb-4">
                    <Link
                      href={
                        isService
                          ? `/${parent.slug}`
                          : `/industry/${parent.slug}`
                      }
                      className="block text-[16px] leading-[170%] px-3 py-1.5 font-semibold text-white capitalize no-underline"
                    >
                      {isService ? parent.menuname : parent.name}
                    </Link>

                    <div className="grid grid-cols-2 gap-3">
                      {parent.children
                        ?.filter(
                          (subchil) =>
                            (subchil?.grandchildren?.length || 0) === 0,
                        )
                        ?.slice(0, 3)
                        ?.map((subChild, subIdx) => (
                          <div key={subIdx}>
                            <Link
                              href={
                                isService
                                  ? `/${parent.slug}/${subChild.slug}`
                                  : `/industry/${subChild.slug}`
                              }
                              className="block text-[16px] leading-[170%] px-3 py-1.5 font-semibold text-white/50 hover:text-white capitalize no-underline"
                            >
                              {isService ? subChild.menuname : subChild.name}
                            </Link>
                          </div>
                        ))}

                      {parent.children
                        ?.filter(
                          (subchil) =>
                            (subchil?.grandchildren?.length || 0) === 0,
                        )
                        ?.slice(3)
                        ?.map((subChild, subIdx) => (
                          <div key={subIdx + 3}>
                            <Link
                              href={
                                isService
                                  ? `/${parent.slug}/${subChild.slug}`
                                  : `/industry/${subChild.slug}`
                              }
                              className="block text-[16px] leading-[170%] px-3 py-1.5 font-semibold text-white/50 hover:text-white capitalize no-underline"
                            >
                              {isService ? subChild.menuname : subChild.name}
                            </Link>
                          </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {parent.children
                        ?.filter(
                          (subchil) =>
                            (subchil?.grandchildren?.length || 0) > 0,
                        )
                        ?.map((subChild, subIdx) => (
                          <div key={subIdx}>
                            <Link
                              href={
                                isService
                                  ? `/${parent.slug}/${subChild.slug}`
                                  : `/industry/${subChild.slug}`
                              }
                              className="block text-[16px] leading-[170%] px-3 py-1.5 font-semibold text-white capitalize no-underline"
                            >
                              {isService ? subChild.menuname : subChild.name}
                            </Link>

                            <div className="space-y-1">
                              {subChild.grandchildren?.map((grand, gidx) => (
                                <Link
                                  key={gidx}
                                  href={
                                    isService
                                      ? `/${parent.slug}/${subChild.slug}/${grand.slug}`
                                      : `/industry/${grand.slug}`
                                  }
                                  className="block text-[16px] leading-[170%] px-3 py-1.5 font-semibold text-white/50 hover:text-white capitalize no-underline"
                                >
                                  {grand.menuname || grand.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MegaMenu;
